import connector from '@/api/connector'
import { EntityType } from '@depanneo/types'
import { AxiosRequestConfig } from 'axios'

type HttpMethod = 'get' | 'post' | 'put' | 'delete' | 'patch'

interface RequestParams<TBody = undefined> {
  method: HttpMethod
  url: string
  body?: TBody
  options: AxiosRequestConfig
}

// Generic function to handle all API requests
export const request = async <TResponse, TBody = undefined>({
  method,
  url,
  body,
  options = {},
}: RequestParams<TBody>): Promise<TResponse> => {
  try {
    let response

    switch (method) {
      case 'get':
        response = await connector.get(url, options)
        break
      case 'post':
        response = await connector.post(url, body, options)
        break
      case 'put':
        response = await connector.put(url, body, options)
        break
      case 'delete':
        response = await connector.delete(url, { ...options, data: body })
        break
      case 'patch':
        response = await connector.patch(url, body, options)
        break
    }

    return response.data
  } catch (error) {
    throw error
  }
}

// Specialized functions for different HTTP methods
export const get = <TResponse>(url: string, options = {}) =>
  request<TResponse>({ method: 'get', url, options })

export const post = <TResponse, TBody>(
  url: string,
  body: TBody,
  options = {}
) => request<TResponse, TBody>({ method: 'post', url, body, options })

export const put = <TResponse, TBody>(url: string, body: TBody, options = {}) =>
  request<TResponse, TBody>({ method: 'put', url, body, options })

export const del = <TResponse>(url: string, options = {}) =>
  request<TResponse>({ method: 'delete', url, options })

export const patch = <TResponse, TBody>(
  url: string,
  body?: TBody,
  options = {}
) => request<TResponse, TBody>({ method: 'patch', url, body, options })

export function markErrorAsHandled(error: unknown): unknown {
  if (error && typeof error === 'object') {
    ;(error as any).__handledByMutation = true
  }
  return error
}

export const toEntityEndpointPath = (entityType: EntityType): string => {
  const pluralMap: Record<EntityType, string> = {
    [EntityType.ORGANIZATION]: 'organizations',
    [EntityType.SITE]: 'sites',
    [EntityType.EQUIPMENT]: 'equipments',
    [EntityType.INTERVENTION]: 'interventions',
    [EntityType.TECHNICIAN]: 'technicians',
    [EntityType.USER]: 'users',
    [EntityType.QUOTE]: 'quotes',
    [EntityType.INVOICE]: 'invoices',
    [EntityType.INTERVENTION_DISPUTE]: 'interventions',
  }
  return pluralMap[entityType]
}

export const toNotesEndpointPath = (
  entityType: EntityType,
  entityId: string
): string => {
  const endpoint = toEntityEndpointPath(entityType)
  if (entityType === EntityType.INTERVENTION_DISPUTE) {
    return `/${endpoint}/${entityId}/dispute-notes`
  }
  return `/${endpoint}/${entityId}/notes`
}
